// Frozen contract for Badge Builder / unit HTML:
// <click-to-reveal config='{"ariaLabel":"...","items":[{"id":"...","trigger":"...","content":"...","image":{"src":"...","alt":"..."}}]}'></click-to-reveal>
//
// `image` is optional and shows below the content. It sits in its own `src` key
// (not an <img> inside `content`) so Th::ImageUrlResolver rewrites its URL.
// `variant` / `columns` in the JSON are ignored. V1 always hydrates sequential.
// Returns the final props for the TDS2 `ClickToReveal` component (with `trigger`
// and `content` already sanitized and wrapped as React nodes), or null so the
// placeholder can stay in the page.

import React from 'react';
import {t} from 'lib/I18n';
import {parseJsonConfig} from 'lib/htmlIsland/parseJsonConfig';
import {sanitizeHtml} from 'lib/htmlIsland/sanitizeHtml';
import {htmlNode} from 'lib/htmlIsland/htmlNode';

const ARIA_LABEL_KEY = 'views.trailhead.unit.click_to_reveal.aria_label';

// Trigger sits inside TDS2's role="button". Only non-interactive phrasing
// content is allowed so we do not nest links or buttons (WCAG 2.2).
const TRIGGER_PURIFY = {
  ALLOWED_TAGS: [
    'abbr',
    'b',
    'bdi',
    'bdo',
    'br',
    'cite',
    'code',
    'data',
    'del',
    'dfn',
    'em',
    'i',
    'img',
    'ins',
    'kbd',
    'mark',
    'q',
    's',
    'samp',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'time',
    'u',
    'var',
    'wbr'
  ],
  ALLOWED_ATTR: ['alt', 'src'],
  KEEP_CONTENT: true
};

// Revealed content may include links, which open in a new tab.
const CONTENT_PURIFY = {ADD_ATTR: ['target']};

// Keep wide screenshots inside the tile.
const IMAGE_STYLE = {display: 'block', maxWidth: '100%', height: 'auto'};

export function parseConfig(element) {
  const parsed = parseJsonConfig(element, {warnLabel: 'click-to-reveal'});
  if (!parsed || !Array.isArray(parsed.items)) {
    return null;
  }

  const items = parsed.items.map(toItem).filter(Boolean);
  if (items.length === 0) {
    return null;
  }

  const label = typeof parsed.ariaLabel === 'string' ? parsed.ariaLabel.trim() : '';
  return {variant: 'sequential', ariaLabel: label || t(ARIA_LABEL_KEY), items};
}

function toItem(item, index) {
  if (item == null || typeof item !== 'object') {
    return null;
  }

  const trigger = String(item.trigger ?? '');
  const content = String(item.content ?? '');
  if (!trigger && !content) {
    return null;
  }

  return {
    id: String(item.id ?? index),
    trigger: htmlNode(sanitizeHtml(trigger, TRIGGER_PURIFY)),
    content: contentNode(content, toImage(item.image))
  };
}

function contentNode(content, image) {
  const html = htmlNode(sanitizeHtml(content, CONTENT_PURIFY), 'div');
  if (!image) {
    return html;
  }
  return React.createElement(
    React.Fragment,
    null,
    html,
    React.createElement('img', {src: image.src, alt: image.alt, style: IMAGE_STYLE})
  );
}

// Like images in regular unit tables, an image without alt text renders as
// decorative (alt=""). Flash cards differ on purpose: their image is the whole
// card face, so a card without alt is dropped.
function toImage(image) {
  if (image == null || typeof image !== 'object' || typeof image.src !== 'string' || !image.src) {
    return null;
  }
  const alt = typeof image.alt === 'string' ? image.alt.trim() : '';
  return {src: image.src, alt};
}
